import cn from 'classnames';
import React, {forwardRef, useEffect, useRef, useState} from 'react';

import Button from '@/components/Button/Button';
import Image from '@/components/Image/Image';
import LoaderCentered from '@/components/Loader/AppLoader/LoaderCentered';
import server from '@/services/server/server';
import helpers from '@/utils/helpers';
import {twx} from '@/utils/tailwindUtils';

import styles from './Video.module.scss';

const Video = (props, ref) => {
    const {
        autoplay,
        className,
        containerClassName,
        controlsClassName,
        controlAriaLabel,
        isControlsVisible = true,
        loop,
        muted,
        onCanPlay,
        onControlsClickCustom,
        onError,
        onTimeUpdate,
        onLoad,
        onLoadedMetadata,
        playsinline,
        thumbnailSrc,
        src,
        onPlay: onPlayFromProps,
        onPause: onPauseFromProps,
        onEnded,
        onWaiting,
        children,
    } = props;

    const videoRef = useRef(ref);
    const [isLoaded, setIsLoaded] = useState(false);

    const [isVideoCanStart, setIsVideoCanStart] = useState(false);
    const [isAnimationFinished, setIsAnimationFinished] = useState(false);
    const [isVideoPlaying, setIsVideoPlaying] = useState(false);

    const onCanPlayFunc = () => {
        helpers.runFunction(onCanPlay);
        setIsVideoCanStart(true);
    };

    const onLoadFunc = () => {
        helpers.runFunction(onLoad);
    };

    const onErrorFunc = () => {
        helpers.runFunction(onError);
    };

    const onTransitionEnd = () => {
        setIsAnimationFinished(true);
    };

    useEffect(() => {
        if (isAnimationFinished) {
            if (autoplay) {
                videoRef.current.play?.();
            }

            onCanPlayFunc();
        }
    }, [isAnimationFinished]);

    useEffect(() => {
        server
            .get(src)
            .then(onLoadFunc)
            .catch(onErrorFunc)
            .finally(() => {
                setIsLoaded(true);
            });
    }, []);

    const onControlButtonClick = () => {
        const video = videoRef.current;

        if (onControlsClickCustom) {
            helpers.runFunction(onControlsClickCustom);
        } else {
            video.paused ? video.play() : video.pause();
        }
    };

    const onPlay = () => {
        setIsVideoPlaying(true);
        onPlayFromProps?.();
    };
    const onPause = () => {
        setIsVideoPlaying(false);
        onPauseFromProps?.();
    };

    return (
        <>
            <div className={cn(styles.VideoContainer, containerClassName)} aria-hidden='true'>
                {isLoaded ? (
                    <>
                        {children}
                        <video
                            autoPlay={autoplay}
                            className={className}
                            loop={loop}
                            onEnded={onEnded}
                            muted={muted}
                            onCanPlay={onCanPlayFunc}
                            onTimeUpdate={onTimeUpdate}
                            onPause={onPause}
                            onPlay={onPlay}
                            playsInline={playsinline}
                            onLoadedMetadata={onLoadedMetadata}
                            ref={ref ? ref : videoRef}
                            src={src}
                            onWaiting={onWaiting}
                        />
                        {isControlsVisible && (
                            <Button
                                className={twx(
                                    'absolute bottom-4 right-4 z-[2] hidden h-11 w-11 cursor-pointer items-center justify-center rounded-full border-2 border-solid border-primary-dark bg-white p-0 md:right-8',
                                    controlsClassName,
                                    {
                                        flex: isLoaded,
                                    }
                                )}
                                onClick={onControlButtonClick}
                                tabIndex='-1'
                                aria-label={controlAriaLabel}
                            >
                                <div
                                    className={cn(styles.ControlButton, {
                                        [styles.Paused]: !isVideoPlaying,
                                    })}
                                />
                            </Button>
                        )}
                    </>
                ) : (
                    <LoaderCentered className={styles.Loader} />
                )}

                <div className={cn(styles.VideoContainer, containerClassName)} aria-hidden='true'>
                    <Image
                        onTransitionEnd={onTransitionEnd}
                        className={cn(styles.Thumbnail, {'opacity-0': isVideoCanStart}, className)}
                        src={thumbnailSrc}
                    />
                </div>
            </div>
        </>
    );
};

export default forwardRef(Video);
